// Front-camera capture shared by face enrollment (3 stills) and face check-in
// (6-frame head-turn burst).
//
//  - asks for the front camera at 1280x960 (falls back to any camera when the
//    device cannot honour the size),
//  - the preview is mirrored with CSS only (`previewStyle`) so the person sees
//    themselves as in a mirror, while every uploaded frame is the camera's own,
//    un-mirrored image,
//  - frames are resized on a canvas to 640 px on the long side and encoded as
//    JPEG (quality 0.85), returned as Blobs the caller uploads and drops —
//    nothing here keeps a frame once it has been handed out,
//  - every track is stopped when the owner unmounts (or calls `stop`).
//
// Development only: `localStorage.faceFakeCamera = "1"` replaces the webcam
// with a drawn, moving placeholder face (canvas.captureStream) so the flows can
// be exercised in a browser without a camera or camera permission. The fake
// face idles with a slow left/right sway; `setFakeMotion(steps, durationMs)`
// makes it perform a head-turn challenge ("left" | "center" | "right" steps,
// evenly spread over durationMs) so the check-in challenge can be verified
// end to end. Sign convention matches the service's "left_positive" yaw: a
// turn to the person's LEFT moves the features toward the image's right in
// the un-mirrored frame (the preview is mirrored, so it looks natural).
import { useCallback, useEffect, useRef, useState } from "react";

export const CAMERA_CONSTRAINTS = {
  audio: false,
  video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } },
};
export const FRAME_LONG_SIDE = 640;
export const JPEG_QUALITY = 0.85;

// Preview is a mirror; uploaded frames are not (see captureFrame).
export const MIRROR_PREVIEW_STYLE = { transform: "scaleX(-1)" };

// idle → starting → ready, or one of the failures.
export const CAMERA_STATUS = {
  IDLE: "idle",
  STARTING: "starting",
  READY: "ready",
  DENIED: "denied",          // NotAllowedError / SecurityError
  UNAVAILABLE: "unavailable", // no camera, or getUserMedia missing (http)
  ERROR: "error",
};

const isDev = process.env.NODE_ENV !== "production";

const fakeCameraWanted = () => {
  if (!isDev) return false;
  try { return localStorage.getItem("faceFakeCamera") === "1"; } catch { return false; }
};

// Requested head-turn for the fake camera (see setFakeMotion).
const fakeMotion = { steps: null, startedAt: 0, durationMs: 0 };
const FAKE_TURN_PX = { left: 60, center: 0, right: -60 };

/** The fake head's x-offset: the requested challenge while one is running, else a slow sway. */
const fakeTurnAt = (nowMs, t) => {
  const steps = fakeMotion.steps;
  if (!steps || !steps.length) return Math.sin(t * 1.2) * 60;
  const elapsed = nowMs - fakeMotion.startedAt;
  if (elapsed > fakeMotion.durationMs + 1500) { fakeMotion.steps = null; return 0; }
  const n = steps.length;
  const pos = Math.min(n - 1 + 0.999, Math.max(0, (elapsed / fakeMotion.durationMs) * n));
  const i = Math.floor(pos);
  const frac = pos - i;
  const from = FAKE_TURN_PX[steps[i]] ?? 0;
  const to = FAKE_TURN_PX[steps[Math.min(n - 1, i + 1)]] ?? 0;
  // hold the pose for the first 60% of each step, then glide to the next one
  const glide = frac < 0.6 ? 0 : (frac - 0.6) / 0.4;
  return from + (to - from) * glide;
};

// A drawn face that slowly turns left and right, so the burst has "motion".
const createFakeStream = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 1280;
  canvas.height = 960;
  const ctx = canvas.getContext("2d");
  let raf = null;
  const started = performance.now();
  const draw = () => {
    const t = (performance.now() - started) / 1000;
    const w = canvas.width, h = canvas.height;
    ctx.fillStyle = "#cfd8e3";
    ctx.fillRect(0, 0, w, h);
    const turn = fakeTurnAt(performance.now(), t); // head turn offset (+ = person's left)
    // shoulders
    ctx.fillStyle = "#2f3e55";
    ctx.beginPath(); ctx.ellipse(w / 2, h * 0.95, 360, 220, 0, 0, Math.PI * 2); ctx.fill();
    // head
    ctx.fillStyle = "#e8b48c";
    ctx.beginPath(); ctx.ellipse(w / 2 + turn * 0.3, h * 0.45, 190, 250, 0, 0, Math.PI * 2); ctx.fill();
    // eyes
    ctx.fillStyle = "#22304a";
    ctx.beginPath(); ctx.ellipse(w / 2 - 70 + turn, h * 0.40, 22, 14, 0, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(w / 2 + 70 + turn, h * 0.40, 22, 14, 0, 0, Math.PI * 2); ctx.fill();
    // nose + mouth
    ctx.strokeStyle = "#b9805a"; ctx.lineWidth = 6;
    ctx.beginPath(); ctx.moveTo(w / 2 + turn * 1.2, h * 0.44); ctx.lineTo(w / 2 + turn * 1.2 - 18, h * 0.52); ctx.stroke();
    ctx.strokeStyle = "#8c3b3b"; ctx.lineWidth = 8;
    ctx.beginPath(); ctx.arc(w / 2 + turn, h * 0.58, 60, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
    // "FAKE CAMERA" watermark (un-mirrored, so it reads mirrored in the preview)
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.font = "bold 44px sans-serif";
    ctx.fillText("FAKE CAMERA", 40, h - 40);
    raf = requestAnimationFrame(draw);
  };
  draw();
  const stream = canvas.captureStream(15);
  const [track] = stream.getVideoTracks();
  const origStop = track.stop.bind(track);
  track.stop = () => { if (raf) cancelAnimationFrame(raf); raf = null; origStop(); };
  return stream;
};

const classifyError = (err) => {
  const name = err?.name || "";
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return CAMERA_STATUS.DENIED;
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") return CAMERA_STATUS.UNAVAILABLE;
  return CAMERA_STATUS.ERROR;
};

const requestStream = async () => {
  if (fakeCameraWanted()) return createFakeStream();
  if (!navigator.mediaDevices?.getUserMedia) {
    const e = new Error("getUserMedia unavailable"); e.name = "NotFoundError"; throw e;
  }
  try {
    return await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
  } catch (err) {
    // The camera exists but cannot do 1280x960 → take whatever it offers.
    if (err?.name === "OverconstrainedError" || err?.name === "ConstraintNotSatisfiedError") {
      return navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: "user" } });
    }
    throw err;
  }
};

/**
 * Resize the current video frame to FRAME_LONG_SIDE on its long side and
 * encode it as JPEG. Returns a Blob (never retained here).
 */
export const frameToBlob = (video, longSide = FRAME_LONG_SIDE, quality = JPEG_QUALITY) =>
  new Promise((resolve, reject) => {
    const vw = video.videoWidth, vh = video.videoHeight;
    if (!vw || !vh) { reject(new Error("video_not_ready")); return; }
    const scale = Math.min(1, longSide / Math.max(vw, vh));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);
    const ctx = canvas.getContext("2d");
    // No transform: the upload is the camera's un-mirrored image.
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("encode_failed"))),
      "image/jpeg",
      quality,
    );
  });

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function useFaceCapture() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mountedRef = useRef(true);
  const [status, setStatus] = useState(CAMERA_STATUS.IDLE);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    const s = streamRef.current;
    streamRef.current = null;
    if (s) s.getTracks().forEach((t) => { try { t.stop(); } catch { /* already stopped */ } });
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch { /* detached */ }
    }
    if (mountedRef.current) setStatus(CAMERA_STATUS.IDLE);
  }, []);

  const attach = useCallback(async (stream) => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    try { await video.play(); } catch { /* autoplay policies: the frame is still drawn */ }
    if (video.readyState < 2) {
      await new Promise((resolve) => {
        const done = () => { video.removeEventListener("loadeddata", done); resolve(); };
        video.addEventListener("loadeddata", done);
        setTimeout(done, 3000);
      });
    }
  }, []);

  const start = useCallback(async () => {
    if (streamRef.current) { await attach(streamRef.current); return true; }
    setStatus(CAMERA_STATUS.STARTING);
    setError(null);
    try {
      const stream = await requestStream();
      if (!mountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return false; }
      streamRef.current = stream;
      await attach(stream);
      if (mountedRef.current) setStatus(CAMERA_STATUS.READY);
      return true;
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        setStatus(classifyError(err));
      }
      return false;
    }
  }, [attach]);

  // Re-attach when the <video> element (re)mounts while a stream is live.
  const setVideoEl = useCallback((el) => {
    videoRef.current = el;
    if (el && streamRef.current && el.srcObject !== streamRef.current) attach(streamRef.current);
  }, [attach]);

  /** One un-mirrored JPEG of the current frame. */
  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !streamRef.current) return Promise.reject(new Error("camera_not_ready"));
    return frameToBlob(video);
  }, []);

  /**
   * `count` frames at even intervals across `durationMs`. `onProgress(i, count)`
   * fires after each frame. Resolves to Blob[]; the caller owns them.
   */
  const captureBurst = useCallback(async (count, durationMs, onProgress) => {
    const frames = [];
    const step = count > 1 ? durationMs / (count - 1) : 0;
    const t0 = performance.now();
    for (let i = 0; i < count; i += 1) {
      const due = t0 + i * step;
      const wait = due - performance.now();
      if (wait > 0) await sleep(wait);
      frames.push(await captureFrame());
      onProgress?.(i + 1, count);
    }
    return frames;
  }, [captureFrame]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const s = streamRef.current;
      streamRef.current = null;
      if (s) s.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
    };
  }, []);

  /** Fake camera only: perform `steps` (["left","center","right"], …) over `durationMs` starting now. No-op with a real camera. */
  const setFakeMotion = useCallback((steps, durationMs) => {
    if (!fakeCameraWanted()) return;
    fakeMotion.steps = Array.isArray(steps) && steps.length ? steps.map((s) => String(s).toLowerCase()) : null;
    fakeMotion.startedAt = performance.now();
    fakeMotion.durationMs = Math.max(1, Number(durationMs) || 0);
  }, []);

  return {
    /** Pass as `ref` of the <video>; the preview is attached automatically. */
    videoRef: setVideoEl,
    status,
    error,
    isReady: status === CAMERA_STATUS.READY,
    isFake: fakeCameraWanted(),
    setFakeMotion,
    start,
    stop,
    captureFrame,
    captureBurst,
    previewStyle: MIRROR_PREVIEW_STYLE,
  };
}

export default useFaceCapture;
